import { useState } from 'react';

import { type ButtonProps, type IconButtonProps, Button, IconButton } from '../button';
import { Dialog, DialogTitle, DialogContent, DialogActions, type DialogProps } from '../dialog';
import { CloseRoundedIcon } from '../icons';
import { Text } from '../text';

interface ConfirmationModalProps extends Omit<DialogProps, 'content'> {
  variant?: ButtonProps['color'];
  title: string;
  content: React.ReactNode;
  onAccept?: ButtonProps['onClick'];
  onDiscard?: ButtonProps['onClick'];
  onClose?: IconButtonProps['onClick'];
  loading?: ButtonProps['loading'];
  acceptText?: string;
  discardText?: string;
  acceptButtonProps?: ButtonProps;
  discardButtonProps?: ButtonProps;
}

function AcceptButton(props: ButtonProps) {
  const { children, onClick, loading: isLoadingProps, sx, ...restProps } = props;
  const [isLoading, setIsLoading] = useState(false);

  const handleAccept: ButtonProps['onClick'] = (event) => {
    (async () => {
      setIsLoading(true);
      await onClick?.(event);
      setIsLoading(false);
    })();
  };
  return (
    <Button
      size='large'
      onClick={handleAccept}
      sx={{ minWidth: '120px', ...sx }}
      loading={isLoading || isLoadingProps}
      {...restProps}
    >
      {children}
    </Button>
  );
}

function ConfirmationModal(props: Readonly<ConfirmationModalProps>) {
  const {
    variant = 'primary',
    title,
    content,
    onAccept,
    onDiscard,
    acceptText,
    discardText,
    loading: isLoading,
    onClose,
    acceptButtonProps,
    discardButtonProps,
    ...restProps
  } = props;

  return (
    <Dialog
      slotProps={{
        paper: {
          sx: { minWidth: '300px', maxHeight: 'fit-content' },
        },
      }}
      maxWidth='xs'
      {...restProps}
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {typeof content === 'string' ? <Text>{content}</Text> : content}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onDiscard}
          color='secondary'
          variant='outlined'
          size='large'
          disabled={!!isLoading}
          {...discardButtonProps}
        >
          {discardText ?? 'Cancel'}
        </Button>
        <AcceptButton onClick={onAccept} color={variant} loading={isLoading} {...acceptButtonProps}>
          {acceptText ?? 'Confirm'}
        </AcceptButton>
      </DialogActions>
      {onClose ? (
        <IconButton
          onClick={onClose}
          sx={(theme) => ({ position: 'absolute', right: theme.spacing(4), top: theme.spacing(4) })}
          size='large'
          disabled={!!isLoading}
        >
          <CloseRoundedIcon />
        </IconButton>
      ) : null}
    </Dialog>
  );
}

export { ConfirmationModal };
export type { ConfirmationModalProps };
